import React, { useEffect, useState } from "react";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { Button, message, Form, Checkbox } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import InputCard from "../../../../../Components/Cards/InputCard";
import {
  getFleetManagerDocumentRequirementById,
  updateFleetManagerDocumentRequirement,
} from "../../../../../API/Admin/SystemConfiguration/FleetManagerDocuments";
import "./style.css";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const CATEGORY_OPTIONS = [
  { label: "Legal", value: "legal" },
  { label: "Compliance", value: "compliance" },
  { label: "Financial", value: "financial" },
  { label: "Operational", value: "operational" },
  { label: "General", value: "general" },
];

const FORMAT_OPTIONS = [
  { label: "PDF", value: "pdf" },
  { label: "JPG", value: "jpg" },
  { label: "PNG", value: "png" },
  { label: "DOCX", value: "docx" },
];

const EditFleetManagerDocument = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [form] = Form.useForm();
  const passedId = location.state?.id;
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!passedId) {
      message.error(
        "No document selected. Please open this page from the Fleet Manager Document Required list.",
      );
      return;
    }

    const fetchDocument = async () => {
      setLoading(true);
      try {
        const res = await getFleetManagerDocumentRequirementById(passedId);
        const data = res?.data?.data;

        if (Math.floor(res?.status / 100) === 2 && data) {
          form.setFieldsValue({
            documentName: data.document_name,
            category: data.category,
            mandatoryAtOnboarding: !!data.required_at_onboarding,
            isRequired: !!data.is_required,
            hasExpiry: !!data.has_expiry,
            allowedFormat: Array.isArray(data.allowed_formats)
              ? data.allowed_formats
              : [],
          });
        } else {
          message.error(res?.data?.message || "Failed to load document.");
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load document."),
        );
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [passedId, form]);

  const handleBack = () => {
    navigate("/system-configuration", {
      state: { activeTab: "Fleet Manager Document Required" },
    });
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const generalInformationFields = [
    {
      name: "documentName",
      label: "Document Name",
      type: "text",
      placeholder: "Enter Document Name",
      rules: [{ required: true, message: "Document name is required" }],
    },
    {
      name: "category",
      label: "Category",
      type: "select",
      placeholder: "Select Category",
      options: CATEGORY_OPTIONS,
      rules: [{ required: true, message: "Category is required" }],
    },
  ];

  const requirementLogicFields = [
    {
      name: "mandatoryAtOnboarding",
      label: "Mandatory at Onboarding",
      description: "User cannot proceed without uploading this.",
      type: "switch",
      fullWidth: true,
    },
    {
      name: "isRequired",
      label: "Required",
      description: "Marks this document as mandatory for approval.",
      type: "switch",
      fullWidth: true,
    },
    {
      name: "hasExpiry",
      label: "Has Expiry Date",
      description: "This document type can have an expiry date tracked.",
      type: "switch",
      fullWidth: true,
    },
  ];

  const handleSubmit = async () => {
    if (!passedId) {
      message.error("No document selected.");
      return;
    }

    let values;
    try {
      values = await form.validateFields();
    } catch (error) {
      message.error("Please fill all fields in the form.");
      return;
    }

    setSaving(true);
    try {
      const res = await updateFleetManagerDocumentRequirement(passedId, {
        document_name: values.documentName,
        category: values.category,
        required_at_onboarding: values.mandatoryAtOnboarding,
        is_required: values.isRequired,
        has_expiry: values.hasExpiry,
        allowed_formats: values.allowedFormat,
      });

      if (Math.floor(res?.status / 100) === 2) {
        message.success(
          getErrorMessage(
            res?.data,
            "Fleet Manager Document updated successfully!",
          ),
        );
        navigate("/system-configuration", {
          state: { activeTab: "Fleet Manager Document Required" },
        });
      } else {
        message.error(getErrorMessage(res?.data, "Failed to update document."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update document."),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="edit-fleet-manager">
      <div className="back-btn">
        <Button icon={<ArrowLeftOutlined />} onClick={handleBack}>
          Back
        </Button>
      </div>
      <div className="title">Edit Document Requirement</div>

      <Form form={form} layout="vertical" disabled={loading}>
        <div className="input-cards-container">
          <InputCard
            title="General Information"
            fields={generalInformationFields}
          />

          <InputCard
            title="Requirement Logic"
            fields={requirementLogicFields}
          />

          <InputCard title="Allowed Formats">
            <Form.Item
              name="allowedFormat"
              label="Accepted File Type"
              rules={[
                { required: true, message: "Please select at least one format" },
              ]}
            >
              <Checkbox.Group options={FORMAT_OPTIONS} />
            </Form.Item>
          </InputCard>
        </div>

        <div className="btns-row">
          <Button onClick={handleCancel} disabled={saving}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={handleSubmit}
            className="save-btn"
            loading={saving}
            disabled={saving}
          >
            Save Changes
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default EditFleetManagerDocument;
